import environment from "../environment";

export class AppConstants {
  static readonly CODE_COOKIE = "code";
  static readonly CSRF_COOKIE = "csrf";
  static readonly TOKEN_ID_COOKIE = "token-id";
  static readonly ACCESS_TOKEN_COOKIE = "access-token";
  static readonly REFRESH_TOKEN_COOKIE = "refresh-token";

  static readonly TOKEN_ID = "tokenId";
  static readonly DATA_TYPE_JSON = "json";

  static readonly TOAST_TYPE_ERROR = "error";
  static readonly TOAST_TYPE_SUCCESS = "success";

  static readonly DEFAULT_INACTIVITY_LOGOUT_TIME =
    environment.inactivityAutoLogoutTime;
  static readonly DEFAULT_INACTIVITY_WARNING_TIME =
    environment.inactivityWarningTime;

  static readonly DEFAULT_PAGE = 1;
  static DEFAULT_PAGE_SIZE = 5;
  static readonly MAXIMUM_PAGE_SIZE = 10000;
  static DEFAULT_PAGE_SIZES = [5, 10, 20];
  static DEFAULT_MIGRATION_LOG_REFRESH_INTERVAL = 10000;
  static readonly MODULE_LIST_DEFAULT_PAGE_SIZE = 5;

  static readonly DEFAULT_TEXT_MAX_LENGTH = 255;

  static readonly DEFAULT_TOAST_SUCCESS_TTL = 3000;
  static readonly DEFAULT_TOAST_ERROR_TTL = 6000;

  static readonly HEADER_X_USERNAME = "X-Username";
  static readonly HEADER_X_PASSWORD = "X-Password";
  static readonly HEADER_CONTENT_TYPE = "Content-Type";
  static readonly HEADER_ACCEPT = "Accept";
  static readonly HEADER_X_LOCALE = "X-Locale";

  static readonly GCS_UPLOAD_ACTION = "UPLOAD";
  static readonly GCS_UPLOAD_DOWNLOAD = "DOWNLOAD";
  static readonly GCS_MAX_EXPIRATION_TIME = 1440;
  static readonly GCS_MIN_EXPIRATION_TIME = 10;

  static readonly HEADER_MAX_AGE = "max-age";
  static readonly VALUE_MAX_AGE_MAXIMUM = 3600;
  static readonly VALUE_MAX_AGE_MINIMUM = 0;

  static readonly HEADER_CACHE_CONTROL = "Cache-Control";
  static readonly VALUE_PUBLIC = "public";
  static readonly VALUE_NO_CACHE = "no-cache";
  static readonly CONTENT_DISPOSITION = "Content-Disposition";

  static readonly VALUE_APPLICATION_PDF = "application/pdf";
  static readonly VALUE_APPLICATION_JSON = "application/json";
  static readonly VALUE_APPLICATION_URL_ENCODED =
    "application/x-www-form-urlencoded";

  static readonly GLOBAL_HEADERS = {
    [this.HEADER_CONTENT_TYPE]: this.VALUE_APPLICATION_JSON,
  };

  static readonly QUERY_PARAM_PAGE = "page";
  static readonly QUERY_PARAM_SIZE = "size";
  static readonly QUERY_PARAM_NAME = "name";
  static readonly QUERY_PARAM_SORT = "sort";
  static readonly QUERY_PARAM_FILTERS = "filters";

  static readonly GCP_FILES = "file";
  static readonly GCP_FOLDERS = "folder";

  static readonly SOLUTION_BANNER_IMG = "solution.png";
  static readonly SOLUTION_BANNER_ICON = "solution.ico";
  static readonly ADMIN_SPLASH_IMG = "splash.png";
  static readonly ADMIN_THEME_PACK = "theme.json";
  static readonly TEMPLATE_FILE = "template.json";

  static readonly DEFAULT_DATE_FORMAT = "MMMM DD, YYYY h:mm A";
  static readonly MOBILE_DATA_FORMAT = "MMM DD YYYY";

  static readonly ZENDESK_AUTHORIZATION_URI =
    "https://%subdomain.zendesk.com/oauth/authorizations/new";

  static readonly BI_CONTACT_PAGE_URL = "https://brightinsight.com/contact-us";

  static readonly ASSET_MANAGEMENT_SERVICE = "asset-management-service";
  static readonly BASE_STORAGE_URL = `https://storage.googleapis.com/${environment.ENV_TENANT_NAME}-${this.ASSET_MANAGEMENT_SERVICE}/assets`;

  static readonly DEFAULT_PRIVACY_URL =
    "https://brightinsight.com/privacy-policy";

  // Helper function to check if multi-client support is enabled
  static isMultiClientSupportEnabled = (): boolean => {
    return sessionStorage.getItem("multiClientSupport") === "true";
  };

  // Helper function to get clientName (only if multi-client support is enabled)
  static getClientName = (): string => {
    return AppConstants.isMultiClientSupportEnabled() 
      ? localStorage.getItem("clientName") || "" 
      : "";
  };

  /**
   * Simple utility to get the base client-aware path
   * Returns: "solution-name" or "solution-name/client-name"
   */
  private static getBasePath = (): string => {
    const clientName = AppConstants.getClientName();
    return AppConstants.isMultiClientSupportEnabled() && clientName
      ? `${environment.ENV_SOLUTION_NAME}/${clientName}`
      : environment.ENV_SOLUTION_NAME;
  };

  // Simplified client-aware getter functions
  static getTranslationSuffix = (): string => {
    return `${AppConstants.getBasePath()}/i18n/languages`;
  };

  static getRulesSuffix = (): string => {
    return `${AppConstants.getBasePath()}/i18n/rules`;
  };

  static getDisclaimerSuffix = (): string => {
    return `${AppConstants.getBasePath()}/i18n/disclaimer`;
  };

  static getAdverseSuffix = (): string => {
    return `${AppConstants.getBasePath()}/i18n/adverse-event`;
  };

  static getGCPPath = (): string => {
    return `assets/${AppConstants.getBasePath()}/`;
  };

  static getRulesGCPPath = (): string => {
    return `assets/${AppConstants.getBasePath()}/i18n/rules/`;
  };

  static getDisclaimerGCPPath = (): string => {
    return `assets/${AppConstants.getBasePath()}/i18n/disclaimer/`;
  };

  static getAdverseGCPPath = (): string => {
    return `assets/${AppConstants.getBasePath()}/i18n/adverse-event/`;
  };

  static getLanguagesGCPPath = (): string => {
    return `assets/${AppConstants.getBasePath()}/i18n/languages/`;
  };

  static getConfigFullPath = (): string => {
    return `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getBasePath()}/config.json`;
  };

  static getTemplateFullPath = (): string => {
    return `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getBasePath()}/${this.TEMPLATE_FILE}`;
  };

  static getLogoIconUrl = (): string => {
    return `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getBasePath()}/${this.SOLUTION_BANNER_ICON}`;
  };

  static getLogoSolutionUrl = (): string => {
    return `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getBasePath()}/${this.SOLUTION_BANNER_IMG}`;
  };

  static getLogoSplashUrl = (): string => {
    return `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getBasePath()}/${this.ADMIN_SPLASH_IMG}`;
  };

  static getThemeUrl = (): string => {
    return `${AppConstants.BASE_STORAGE_URL}/${AppConstants.getBasePath()}/${this.ADMIN_THEME_PACK}`;
  };

  // Static getters that replace the old static readonly properties for client-aware paths
  static get ASSETS_LOCALISATION_GCP_PATH(): string {
    return AppConstants.getLanguagesGCPPath();
  }

  static get ASSETS_LOCALISATION_RULES_GCP_PATH(): string {
    return AppConstants.getRulesGCPPath();
  }

  static get ASSETS_LOCALISATION_DISCLAIMER_GCP_PATH(): string {
    return AppConstants.getDisclaimerGCPPath();
  }

  static get ASSETS_LOCALISATION_ADVERSE_GCP_PATH(): string {
    return AppConstants.getAdverseGCPPath();
  }

  static get DATA_REQUEST_APP_TEMPLATE_FULL_PATH(): string {
    return AppConstants.getTemplateFullPath();
  }

  static get LOGO_ICON_URL(): string {
    return AppConstants.getLogoIconUrl();
  }

  static get LOGO_SOLUTION_URL(): string {
    return AppConstants.getLogoSolutionUrl();
  }

  static get LOGO_SPLASH_URL(): string {
    return AppConstants.getLogoSplashUrl();
  }

  static get THEME_URL(): string {
    return AppConstants.getThemeUrl();
  }

  // Static constants that don't need client-aware logic
  static readonly DATA_REQUEST_APP_CONFIG_FILE_NAME = `config.json`;
  static readonly LOCALISATION_NAMESPACE_TRANSLATION = "translation";
  static readonly LOCALISATION_NAMESPACE_RULES = "rules";
  static readonly LOCALISATION_NAMESPACE_DISCLAIMER = "disclaimer";
  static readonly LOCALISATION_NAMESPACE_ADVERSE = "adverse";
  static readonly ASSETS_LOCALISATION_FILE_PREFIX = `translation_`;
  static readonly ASSETS_LOCALISATION_RULES_FILE_PREFIX = `rules_`;
  static readonly ASSETS_LOCALISATION_DISCLAIMER_FILE_PREFIX = `disclaimer_`;
  static readonly ASSETS_LOCALISATION_ADVERSE_FILE_PREFIX = `adverse_`;
  static readonly ASSETS_LOCALISATION_DEFAULT_LANGUAGE = "en";

  static readonly USER_LOCKED_OUT_MSG = "User Locked Out.";
  static readonly MAX_LENGTH_IMPORT_DESCRIPTION = 4096;

  static readonly ZENDESK_SYSTEM_FIELDS = [
    "subject",
    "description",
    "assignee",
  ];

  static readonly VALUE_TEXT_PLAIN = "text/plain";

  static readonly ZENDESK_FIELDS_MAPPING = {
    Username: "",
    Type: "",
    "Subject Name": "",
    "Recification Message": "",
    "Restriction Type": "",
    "Additional Message": "",
  };
  static readonly APIGEE_EU_GATEWAY_DOMAINS = {
    dev: "https://api-eu-dev.brightinsight.com",
    qa: "https://api-eu-qa.brightinsight.com",
    stage: "https://api-eu-staging.brightinsight.com",
    prod: "https://api-eu.brightinsight.com",
  };

  static readonly APIGEE_US_GATEWAY_DOMAINS = {
    dev: "https://api-us-dev.brightinsight.com",
    qa: "https://api-us-qa.brightinsight.com",
    stage: "https://api-us-staging.brightinsight.com",
    prod: "https://api-us.brightinsight.com",
  };

  static readonly DISABLED_ROLES_CUSTOMIZATION = ["Patient"];
  static readonly ENABLED_ROLES_CUSTOMIZATION = ["IT_ADMIN"];

  static SUPPORTED_ADVERSE_COUNTRIES = ["us", "ar", "cl", "ca", "mx", "ja"];

  static readonly LOCAL_STORAGE_LOCALE_KEY = "i18nextLng";
  static readonly LOCAL_STORAGE_COUNTRY_KEY = "i18nextCountry";
  static readonly LOCAL_STORAGE_DIRECTION_KEY = "i18nextDirection";

  // RTL Language Support
  static readonly RTL_LANGUAGES = ["ar", "he", "fa", "ur"];
  static readonly ARABIC_LOCALES = [
    "ar",
    "ar-SA",
    "ar-AE",
    "ar-EG",
    "ar-JO",
    "ar-KW",
    "ar-LB",
    "ar-QA",
  ];
  static readonly DEFAULT_RTL_LOCALE = "ar-SA";
  static readonly DIRECTION_RTL = "rtl";
  static readonly DIRECTION_LTR = "ltr";

  // Arabic Culture Specific
  static readonly ARABIC_FONT_FAMILIES = [
    "Noto Sans Arabic",
    "Cairo",
    "Amiri",
    "Tajawal",
  ];
  static readonly ARABIC_NUMBER_FORMAT = "ar-SA-u-nu-arab";

  // Static getters for backward compatibility
  static get ASSETS_GCP_PATH(): string {
    return AppConstants.getGCPPath();
  }

  // Original static paths (for backward compatibility)
  static get BASE_STORAGE_URL_TRANSLATION_SUFFIX(): string {
    return AppConstants.getTranslationSuffix();
  }

  static get BASE_STORAGE_URL_RULES_SUFFIX(): string {
    return AppConstants.getRulesSuffix();
  }

  static get BASE_STORAGE_URL_DISCLAIMER_SUFFIX(): string {
    return AppConstants.getDisclaimerSuffix();
  }

  static get BASE_STORAGE_URL_ADVERSE_SUFFIX(): string {
    return AppConstants.getAdverseSuffix();
  }
}
